import { z } from 'zod';
import { AudienceSegment, CampaignObjective, Channel, ConversionDestination } from '../enums';
import { moneyCentsSchema } from './common';

/* ------------------------------------------------------------------------- *
 * FORMATO INTERNO COMUM
 * O núcleo nunca fala "Meta" nem "Google": fala este formato. Os adaptadores
 * traduzem para a Marketing API / Google Ads API.
 * ------------------------------------------------------------------------- */

export const geoTargetSchema = z.object({
  city: z.string().max(120),
  state: z.string().length(2),
  radiusKm: z.number().min(1).max(80).nullable(),
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
});
export type GeoTarget = z.infer<typeof geoTargetSchema>;

export const audienceTargetSchema = z.object({
  ageMin: z.number().int().min(18).max(65),
  ageMax: z.number().int().min(18).max(65),
  genders: z.array(z.enum(['ALL', 'MALE', 'FEMALE'])).max(3),
  interests: z.array(z.string().max(120)).max(30),
  excludedInterests: z.array(z.string().max(120)).max(30),
  customAudienceIds: z.array(z.string().max(64)).max(20),
  excludeExistingCustomers: z.boolean(),
});
export type AudienceTarget = z.infer<typeof audienceTargetSchema>;

export const adCopySchema = z.object({
  /** Meta: primary_text. Google: descrição longa. */
  primaryText: z.string().max(2000),
  /** Títulos curtos. Google RSA exige até 15, máx. 30 caracteres cada. */
  headlines: z.array(z.string().max(60)).max(15),
  descriptions: z.array(z.string().max(120)).max(4),
  callToAction: z.string().max(40),
  displayUrlPath: z.array(z.string().max(15)).max(2),
});
export type AdCopy = z.infer<typeof adCopySchema>;

export const adDraftSchema = z.object({
  name: z.string().max(160),
  copy: adCopySchema,
  /** ID do creative da plataforma (tabela creatives). Null enquanto não gerado. */
  creativeId: z.string().nullable(),
  finalUrl: z.string().max(2048),
  trackingTemplate: z.string().max(2048).nullable(),
  utm: z.object({
    source: z.string().max(80),
    medium: z.string().max(80),
    campaign: z.string().max(120),
    content: z.string().max(120),
    term: z.string().max(120),
  }),
});
export type AdDraft = z.infer<typeof adDraftSchema>;

export const keywordSchema = z.object({
  text: z.string().max(120),
  matchType: z.enum(['EXACT', 'PHRASE', 'BROAD']),
});
export type Keyword = z.infer<typeof keywordSchema>;

export const adSetDraftSchema = z.object({
  name: z.string().max(160),
  dailyBudgetCents: z.number().int().nullable(),
  geo: z.array(geoTargetSchema),
  audience: audienceTargetSchema,
  /** Só Google Search: palavras-chave do grupo de anúncios. */
  keywords: z.array(keywordSchema).max(200),
  negativeKeywords: z.array(z.string().max(120)).max(200),
  ads: z.array(adDraftSchema).max(20),
});
export type AdSetDraft = z.infer<typeof adSetDraftSchema>;

export const campaignDraftSchema = z.object({
  name: z.string().max(200),
  objective: z.nativeEnum(CampaignObjective),
  segment: z.nativeEnum(AudienceSegment),
  destination: z.nativeEnum(ConversionDestination),
  dailyBudgetCents: z.number().int().min(0),
  /** Meta: CBO no nível de campanha. Google: budget compartilhado. */
  budgetAtCampaignLevel: z.boolean(),
  bidStrategy: z.enum([
    'LOWEST_COST',
    'COST_CAP',
    'MAXIMIZE_CONVERSIONS',
    'TARGET_CPA',
    'MANUAL_CPC',
  ]),
  targetCpaCents: z.number().int().nullable(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  adSets: z.array(adSetDraftSchema).min(1).max(20),
});
export type CampaignDraft = z.infer<typeof campaignDraftSchema>;

/** Saída completa da IA para um projeto de campanha. */
export const campaignGenerationSchema = z.object({
  channel: z.nativeEnum(Channel),
  rationale: z.string().max(3000),
  campaigns: z.array(campaignDraftSchema).min(1).max(5),
  warnings: z.array(z.string().max(500)).max(20),
});
export type CampaignGeneration = z.infer<typeof campaignGenerationSchema>;

/* ------------------------------------------------------------------------- *
 * ENTRADA DO USUÁRIO (briefing)
 * ------------------------------------------------------------------------- */

export const campaignBriefSchema = z.object({
  clientId: z.string().uuid(),
  channel: z.nativeEnum(Channel),
  segment: z.nativeEnum(AudienceSegment),
  objective: z.nativeEnum(CampaignObjective),
  planIds: z.array(z.string().uuid()).min(1).max(10),
  serviceAreaIds: z.array(z.string().uuid()).min(1).max(50),
  monthlyBudgetCents: moneyCentsSchema,
  destination: z.nativeEnum(ConversionDestination),
  destinationUrl: z.string().url().optional(),
  whatsappNumber: z.string().max(20).optional(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date().optional(),
  campaignCount: z.number().int().min(1).max(5).default(1),
  adSetsPerCampaign: z.number().int().min(1).max(6).default(2),
  adsPerAdSet: z.number().int().min(1).max(5).default(2),
  extraInstructions: z.string().max(3000).optional(),
});
export type CampaignBriefInput = z.infer<typeof campaignBriefSchema>;

/* ------------------------------------------------------------------------- *
 * LIMITES DAS PLATAFORMAS — validados ANTES de qualquer chamada externa.
 * ------------------------------------------------------------------------- */

export const PLATFORM_LIMITS = {
  META: {
    primaryTextMax: 500,
    headlineMax: 40,
    descriptionMax: 30,
    minDailyBudgetCents: 600, // ~R$6,00/dia
    maxAdSetsPerCampaign: 100,
    maxAdsPerAdSet: 50,
    maxGeoLocations: 200,
    ageMin: 18,
    ageMax: 65,
  },
  GOOGLE: {
    headlineMax: 30,
    headlineMinCount: 3,
    headlineMaxCount: 15,
    descriptionMax: 90,
    descriptionMinCount: 2,
    descriptionMaxCount: 4,
    pathMax: 15,
    minDailyBudgetCents: 1000,
    maxKeywordsPerAdGroup: 200,
    maxAdGroupsPerCampaign: 100,
  },
} as const;

export const approveCampaignSchema = z.object({
  note: z.string().max(2000).optional(),
  /** Ativar imediatamente após publicar (exige permissão campaign:activate). */
  activateAfterPublish: z.boolean().default(false),
});
export type ApproveCampaignInput = z.infer<typeof approveCampaignSchema>;

export const budgetChangeSchema = z.object({
  newDailyBudgetCents: moneyCentsSchema,
  reason: z.string().min(5).max(1000),
});
export type BudgetChangeInput = z.infer<typeof budgetChangeSchema>;
